import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { AdminGuard } from './components/AdminGuard';
import { RequireAccess } from './components/RequireAccess';
import { RequireAuth } from './components/RequireAuth';
import { RequireConsent } from './components/RequireConsent';
import { AppShell } from './components/shell/AppShell';
import { CreditsPage } from './pages/CreditsPage';
import { DraftPage } from './pages/DraftPage';
import { HomePage } from './pages/HomePage';
import { InvitePage } from './pages/InvitePage';
import { OwnTakePage } from './pages/OwnTakePage';
import { RenderPage } from './pages/RenderPage';
import { VoicePage } from './pages/VoicePage';
import { VoiceRecordPage } from './pages/VoiceRecordPage';
import { LibraryPage } from './pages/LibraryPage';
import { SettingsPage } from './pages/SettingsPage';
import { ThoughtPage } from './pages/ThoughtPage';
import { AdminBroadcastPage } from './pages/admin/AdminBroadcastPage';
import { AdminCostsPage } from './pages/admin/AdminCostsPage';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminEvalNewPage } from './pages/admin/AdminEvalNewPage';
import { AdminEvalPage } from './pages/admin/AdminEvalPage';
import { AdminEvalRunPage } from './pages/admin/AdminEvalRunPage';
import { AdminInvitesPage } from './pages/admin/AdminInvitesPage';
import { AdminPacksPage } from './pages/admin/AdminPacksPage';
import { AdminParticipantsPage } from './pages/admin/AdminParticipantsPage';
import { AdminPaymentsPage } from './pages/admin/AdminPaymentsPage';
import { AdminPromptStepPage } from './pages/admin/AdminPromptStepPage';
import { AdminPromptsPage } from './pages/admin/AdminPromptsPage';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';
import { AdminWaitlistPage } from './pages/admin/AdminWaitlistPage';

const admin = (page: ReactNode) => <AdminGuard>{page}</AdminGuard>;

/** The old «Стиль и голос» address (links in a cached app or in the chat): the voice is on «Озвучка» now. */
function ToVoice() {
  const { draftId } = useParams();
  return <Navigate to={`/drafts/${draftId}/voice`} replace />;
}

export const router = createBrowserRouter([
  {
    element: (
      <RequireAuth>
        <RequireAccess>
          <RequireConsent>
            <AppShell />
          </RequireConsent>
        </RequireAccess>
      </RequireAuth>
    ),
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/credits', element: <CreditsPage /> },
      { path: '/invite', element: <InvitePage /> },
      { path: '/voice/record', element: <VoiceRecordPage /> },
      { path: '/drafts/:draftId/voice', element: <VoicePage /> },
      { path: '/drafts/:draftId/style', element: <ToVoice /> },
      { path: '/drafts/:draftId/own', element: <OwnTakePage /> },
      { path: '/renders/:id', element: <RenderPage /> },
      { path: '/thoughts/:id', element: <ThoughtPage /> },
      { path: '/drafts/:id', element: <DraftPage /> },
      { path: '/library', element: <LibraryPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/admin', element: admin(<AdminDashboard />) },
      { path: '/admin/invites', element: admin(<AdminInvitesPage />) },
      { path: '/admin/waitlist', element: admin(<AdminWaitlistPage />) },
      { path: '/admin/participants', element: admin(<AdminParticipantsPage />) },
      { path: '/admin/packs', element: admin(<AdminPacksPage />) },
      { path: '/admin/payments', element: admin(<AdminPaymentsPage />) },
      { path: '/admin/settings', element: admin(<AdminSettingsPage />) },
      { path: '/admin/broadcast', element: admin(<AdminBroadcastPage />) },
      { path: '/admin/prompts', element: admin(<AdminPromptsPage />) },
      { path: '/admin/prompts/:code', element: admin(<AdminPromptStepPage />) },
      { path: '/admin/costs', element: admin(<AdminCostsPage />) },
      { path: '/admin/eval', element: admin(<AdminEvalPage />) },
      { path: '/admin/eval/new', element: admin(<AdminEvalNewPage />) },
      { path: '/admin/eval/runs/:id', element: admin(<AdminEvalRunPage />) },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
